import React from "react";
import ReactDOM from "react-dom";
import "./MOptionsMenu.css";
import { IconDotsVertical } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";

export interface DropdownOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
  isVisible?: boolean;
  delete?: boolean;
}

export interface MOptionsMenuProps {
  options: DropdownOption[];
  variant?: "default" | "card";
  disabled?: boolean;
  iconSize?: number;
}

const MOptionsMenu: React.FC<MOptionsMenuProps> = ({
  options,
  variant = "default",
  disabled = false,
  iconSize = 24,
}) => {
  const [open, setOpen] = React.useState(false);
  const [openUpwards, setOpenUpwards] = React.useState(false);
  // Actual rendered menu width, measured post-layout — the CSS sizes the
  // menu to its content (up to 320px), which can be wider than any fixed
  // guess, so positioning must be based on the real width or the menu can
  // bleed off the right edge of the screen (e.g. long "Remove from shopping
  // list" labels).
  const [menuLeft, setMenuLeft] = React.useState<number | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuLeft(null);
      return;
    }

    const buttonRect = buttonRef.current?.getBoundingClientRect();
    const menuRect = ref.current?.getBoundingClientRect();
    if (!buttonRect || !menuRect) return;

    const margin = 8;
    const minLeft = margin + window.scrollX;
    const maxLeft =
      window.innerWidth - menuRect.width - margin + window.scrollX;
    const naturalLeft = buttonRect.right + window.scrollX - menuRect.width;
    setMenuLeft(Math.max(minLeft, Math.min(naturalLeft, maxLeft)));
  }, [open, options.length]);

  React.useEffect(() => {
    if (!open) return;

    const buttonRect = buttonRef.current?.getBoundingClientRect();
    if (!buttonRect) return;

    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    setOpenUpwards(spaceAbove > spaceBelow);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setOpen(false);
  };

  return (
    <div style={{ display: "inline-block", position: "relative" }}>
      <button
        ref={buttonRef}
        style={{
          background: "none",
          border: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          padding: 8,
          opacity: disabled ? 0.5 : 1,
        }}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        aria-label="Show options menu"
        disabled={disabled}
      >
        <IconDotsVertical
          size={iconSize}
          color={disabled ? "#AEAEAE" : undefined}
        />
      </button>

      {!disabled &&
        typeof window !== "undefined" &&
        buttonRef.current &&
        ReactDOM.createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                ref={ref}
                className={`moptions-menu${openUpwards ? " open-up" : ""}`}
                initial={{ opacity: 0, y: openUpwards ? 10 : -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: openUpwards ? 10 : -10 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                style={{
                  position: "absolute",
                  top: openUpwards
                    ? buttonRef.current.getBoundingClientRect().top +
                      window.scrollY -
                      (options.length * 40 + (options.length - 1) * 1)
                    : buttonRef.current.getBoundingClientRect().bottom +
                      window.scrollY,
                  left:
                    menuLeft ??
                    buttonRef.current.getBoundingClientRect().right -
                      (variant === "card" ? 202 : 168) +
                      window.scrollX,
                  zIndex: 2000,
                }}
              >
                {options
                  .filter((opt) => opt.isVisible !== false)
                  .map((option, idx, arr) => (
                    <React.Fragment key={option.id}>
                      <button
                        className={`moptions-menu__item${
                          option.delete ? " delete" : ""
                        }${option.className ? " " + option.className : ""}`}
                        onClick={() => handleOptionClick(option.onClick)}
                      >
                        {option.icon && (
                          <span className="moptions-menu__icon">
                            {option.icon}
                          </span>
                        )}
                        <span>{option.label}</span>
                      </button>
                      {idx < arr.length - 1 && (
                        <div className="moptions-menu__separator" />
                      )}
                    </React.Fragment>
                  ))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default MOptionsMenu;
